import { ILayoutBaseTestGen } from '../layoutBaseTest';
import {
  PdfGen,
  PdfFont,
  PdfRect,
  PdfBrush,
  ETextAlign,
  PdfUnit,
  PdfSize,
  PdfColor,
  PdfPen,
} from '../../pdf/pdf';
import '../../pdf/draw/string';
import '../../pdf/draw/primitives';
import '../../pdf/latex/draw';
import { GUtil } from '../../pdf/gutil';
import { OptsBaseTest } from '../optsBaseTest';

const FONT_SIZE = 16;
const LABEL_SIZE = 8;
const MM = PdfUnit.FromMillimeters;
const MARGIN = MM(12);

interface ExRow { label: string; latex: string }

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

function addPage(pdf: PdfGen, title: string): ReturnType<typeof pdf.addPage> {
  const page = pdf.addPage();
  const font = new PdfFont(PdfFont.FONT_NAME, 11);
  const titleRect = new PdfRect(MARGIN, MM(5), PdfSize.A4.width - 2 * MARGIN, MM(8));
  page.drawString(title, font, PdfBrush.Black, null, titleRect, ETextAlign.CenterLeft);
  return page;
}

function drawRows(
  page: ReturnType<typeof PdfGen.prototype.addPage>,
  rows: ExRow[],
  startY: number,
  rowH = MM(15),
  labelW = MM(38),
  displayStyle = false
): void {
  const labelFont = new PdfFont(PdfFont.FONT_NAME, LABEL_SIZE);
  const innerW = PdfSize.A4.width - 2 * MARGIN;
  const cutPen = new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5);

  let y = startY;
  for (const ex of rows) {
    const labelRect = new PdfRect(MARGIN, y, labelW, rowH);
    const mathRect = new PdfRect(MARGIN + labelW + MM(2), y, innerW - labelW - MM(2), rowH);
    page.drawString(ex.label, labelFont, PdfBrush.Black, null, labelRect, ETextAlign.CenterLeft);
    page.drawRectangle(cutPen, null, mathRect);
    page.drawLatex(
      ex.latex,
      mathRect,
      ETextAlign.CenterLeft,
      FONT_SIZE,
      PdfBrush.Black,
      displayStyle
    );
    y += rowH + MM(1);
  }
}

// ---------------------------------------------------------------------------
// Page 1 — Basic math & fractions
// ---------------------------------------------------------------------------

const PAGE1: ExRow[] = [
  { label: 'Arithmetic', latex: String.raw`1 + 2 = 3` },
  { label: 'Subtraction', latex: String.raw`a - b \neq c + d` },
  { label: 'Power', latex: String.raw`x^2 + y^2 = z^2` },
  { label: 'Sub + Sup', latex: String.raw`a_n^2 = a_{n-1}^2 + 2a_{n-1}d + d^2` },
  { label: 'Fraction', latex: String.raw`\frac{1}{2} + \frac{1}{3} = \frac{5}{6}` },
  { label: 'Fraction 2', latex: String.raw`\frac{a + b}{c - d}` },
  { label: 'Nested frac', latex: String.raw`\frac{\frac{a}{b}}{\frac{c}{d}} = \frac{ad}{bc}` },
  { label: 'Quadratic', latex: String.raw`x = \frac{-b \pm \sqrt{b^2 - 4ac}}{2a}` },
  { label: 'Sqrt', latex: String.raw`\sqrt{4} = 2, \quad \sqrt[3]{8} = 2` },
  { label: 'Nested sqrt', latex: String.raw`\sqrt{x^2 + y^2}` },
  { label: 'Prime', latex: String.raw`f'(x) = \lim_{h \to 0} \frac{f(x+h) - f(x)}{h}` },
  { label: 'Binomial', latex: String.raw`(a + b)^n = \sum_{k=0}^{n} \binom{n}{k} a^k b^{n-k}` },
  { label: 'Overline', latex: String.raw`\overline{AB} \parallel \overline{CD}` },
  { label: 'Underline', latex: String.raw`\underline{x} + \underline{y}` },
];

// ---------------------------------------------------------------------------
// Page 2 — Greek & letterlike symbols
// ---------------------------------------------------------------------------

const PAGE2: ExRow[] = [
  {
    label: 'Greek lower',
    latex: String.raw`\alpha,\ \beta,\ \gamma,\ \delta,\ \epsilon,\ \zeta,\ \eta,\ \theta`,
  },
  {
    label: 'Greek lower 2',
    latex: String.raw`\iota,\ \kappa,\ \lambda,\ \mu,\ \nu,\ \xi,\ \pi,\ \rho`,
  },
  {
    label: 'Greek lower 3',
    latex: String.raw`\sigma,\ \tau,\ \upsilon,\ \phi,\ \chi,\ \psi,\ \omega`,
  },
  {
    label: 'Greek upper',
    latex: String.raw`\Gamma,\ \Delta,\ \Theta,\ \Lambda,\ \Xi,\ \Pi,\ \Sigma,\ \Phi,\ \Psi,\ \Omega`,
  },
  {
    label: 'Variants',
    latex: String.raw`\vartheta,\ \varepsilon,\ \varphi,\ \varsigma,\ \varpi,\ \varrho`,
  },
  { label: 'Euler formula', latex: String.raw`e^{i\pi} + 1 = 0` },
  { label: 'Gauss', latex: String.raw`\sum_{k=0}^{n} k = \frac{n(n+1)}{2}` },
  {
    label: 'Number sets',
    latex: String.raw`\mathbb{N} \subset \mathbb{Z} \subset \mathbb{Q} \subset \mathbb{R} \subset \mathbb{C}`,
  },
  { label: 'Letterlike', latex: String.raw`\hbar,\ \ell,\ \Re,\ \Im,\ \wp,\ \aleph` },
  { label: 'Constants', latex: String.raw`e = \lim_{n \to \infty} \left(1 + \frac{1}{n}\right)^n` },
  { label: 'Geometry', latex: String.raw`\angle ABC + \angle BCA + \angle CAB = \pi` },
  {
    label: 'Nabla',
    latex: String.raw`\nabla f = \frac{\partial f}{\partial x}\hat{x} + \frac{\partial f}{\partial y}\hat{y}`,
  },
];

// ---------------------------------------------------------------------------
// Page 3 — Operators & relations
// ---------------------------------------------------------------------------

const PAGE3: ExRow[] = [
  { label: 'Comparison', latex: String.raw`a \leq b \leq c, \quad a \geq d \geq e` },
  { label: 'Approximate', latex: String.raw`\pi \approx 3.14159, \quad e \approx 2.71828` },
  { label: 'Equiv & sim', latex: String.raw`a \equiv b \pmod{n}, \quad f \sim g` },
  { label: 'Less/greater', latex: String.raw`a \ll b \ll c, \quad x \gg y` },
  { label: 'Much less', latex: String.raw`\epsilon \ll 1 \ll N` },
  { label: 'Proportional', latex: String.raw`F \propto \frac{1}{r^2}` },
  { label: 'Conjunction', latex: String.raw`P \wedge Q \Rightarrow R, \quad P \vee Q` },
  { label: 'Quantifiers', latex: String.raw`\forall x \in \mathbb{R},\ \exists y : y > x` },
  { label: 'Set ops', latex: String.raw`A \cup B = B \cup A, \quad A \cap B = B \cap A` },
  {
    label: 'Membership',
    latex: String.raw`x \in A \setminus B \Rightarrow x \in A \wedge x \notin B`,
  },
  { label: 'Subset', latex: String.raw`A \subseteq B \Leftrightarrow \forall x \in A: x \in B` },
  {
    label: 'Oplus/otimes',
    latex: String.raw`\mathbb{Z}_2 = \{0, 1\}, \quad 1 \oplus 1 = 0, \quad 1 \otimes 1 = 1`,
  },
  { label: 'Therefore', latex: String.raw`a = b, \quad b = c \therefore a = c` },
];

// ---------------------------------------------------------------------------
// Page 4 — Arrows
// ---------------------------------------------------------------------------

const PAGE4: ExRow[] = [
  { label: 'Simple arrows', latex: String.raw`a \rightarrow b \leftarrow c \leftrightarrow d` },
  { label: 'Up/down', latex: String.raw`f\uparrow, \quad g\downarrow, \quad h\updownarrow` },
  { label: 'Double arrows', latex: String.raw`P \Rightarrow Q \Leftarrow R \Leftrightarrow S` },
  { label: 'Dbl up/down', latex: String.raw`A \Uparrow B, \quad C \Downarrow D` },
  {
    label: 'Diagonal',
    latex: String.raw`A \nearrow B, \quad C \searrow D, \quad E \swarrow F, \quad G \nwarrow H`,
  },
  { label: 'Long arrows', latex: String.raw`A \longrightarrow B \longleftarrow C` },
  { label: 'Long double', latex: String.raw`P \Longrightarrow Q, \quad R \Longleftrightarrow S` },
  { label: 'Mapsto', latex: String.raw`f: x \mapsto x^2, \quad g: x \mapsto \sin x` },
  { label: 'Hook arrows', latex: String.raw`A \hookrightarrow B, \quad C \hookleftarrow D` },
  { label: 'Harpoons', latex: String.raw`v \rightharpoonup u, \quad w \leftharpoonup z` },
  { label: 'Rightlefth.', latex: String.raw`A \rightleftharpoons B` },
  { label: 'Implies/iff', latex: String.raw`P \implies Q, \quad P \iff Q` },
  { label: 'Logic chain', latex: String.raw`A \Rightarrow B \Rightarrow C \Rightarrow D` },
];

// ---------------------------------------------------------------------------
// Page 5 — Functions, limits, integrals (display style)
// ---------------------------------------------------------------------------

const PAGE5: ExRow[] = [
  { label: 'Functions', latex: String.raw`\sin^2 x + \cos^2 x = 1` },
  {
    label: 'Trig',
    latex: String.raw`\tan x = \frac{\sin x}{\cos x}, \quad \sec x = \frac{1}{\cos x}`,
  },
  {
    label: 'Inverse trig',
    latex: String.raw`\arcsin(\sin x) = x, \quad \arctan\left(\frac{y}{x}\right)`,
  },
  { label: 'Hyperbolic', latex: String.raw`\cosh^2 x - \sinh^2 x = 1` },
  { label: 'Logarithm', latex: String.raw`\log_{a}(xy) = \log_a x + \log_a y` },
  { label: 'Exp & ln', latex: String.raw`\ln(e^x) = x, \quad e^{\ln x} = x` },
  { label: 'Limit', latex: String.raw`\lim_{x \to 0} \frac{\sin x}{x} = 1` },
  { label: 'Inf. series', latex: String.raw`\sum_{n=1}^{\infty} \frac{1}{n^2} = \frac{\pi^2}{6}` },
  { label: 'Product', latex: String.raw`\prod_{k=1}^{n} k = n!` },
  { label: 'Integral', latex: String.raw`\int_0^\infty e^{-x^2}\,dx = \frac{\sqrt{\pi}}{2}` },
  { label: 'Definite int.', latex: String.raw`\int_a^b f'(x)\,dx = f(b) - f(a)` },
  {
    label: 'Double int.',
    latex: String.raw`\iint_D \left(\frac{\partial Q}{\partial x} - \frac{\partial P}{\partial y}\right)\,dA`,
  },
];

// ---------------------------------------------------------------------------
// Page 6 — Accents, dots, misc
// ---------------------------------------------------------------------------

const PAGE6: ExRow[] = [
  { label: 'Hat', latex: String.raw`\hat{x} + \hat{y} = \hat{z}` },
  { label: 'Vec', latex: String.raw`\vec{v} = v_x \hat{x} + v_y \hat{y}` },
  { label: 'Bar', latex: String.raw`\bar{x} = \frac{1}{n}\sum_{i=1}^n x_i` },
  {
    label: 'Tilde',
    latex: String.raw`\tilde{f}(\xi) = \int_{-\infty}^{\infty} f(x) e^{-2\pi i x\xi}\,dx`,
  },
  { label: 'Dot / ddot', latex: String.raw`\dot{x} = v, \quad \ddot{x} = a` },
  { label: 'Acute/grave', latex: String.raw`\acute{e},\ \grave{e},\ \check{v},\ \breve{u}` },
  { label: 'Dots', latex: String.raw`a_1, a_2, \cdots, a_n \quad \vdots \quad \ddots` },
  { label: 'Therefore/bc', latex: String.raw`a = b \therefore b = a, \quad a = b \because b = a` },
  {
    label: 'Font sizes',
    latex: String.raw`{\tiny tiny}\ {\small small}\ \text{normal}\ {\large large}\ {\Large Large}\ {\huge huge}`,
  },
  { label: 'Spaces', latex: String.raw`a\,b\;c\quad d\qquad e` },
  { label: 'Dashes', latex: String.raw`x\text{-}y\text{ -- }\text{em}---\text{dash}` },
  { label: 'Text in math', latex: String.raw`v = \text{distance} \div \text{time}` },
  { label: 'Phantom', latex: String.raw`x + \phantom{y +} z` },
];

// ---------------------------------------------------------------------------
// Page 7 — Brackets / delimiters
// ---------------------------------------------------------------------------

const PAGE7: ExRow[] = [
  { label: 'Parentheses', latex: String.raw`\left( \frac{a}{b} \right)^2` },
  { label: 'Square', latex: String.raw`\left[ \frac{n(n+1)}{2} \right]^2 = \sum_{k=1}^n k^3` },
  { label: 'Curly', latex: String.raw`f(x) = \left\{ \frac{x^2}{2}, \quad x \geq 0 \right.` },
  {
    label: 'Abs value',
    latex: String.raw`\left| x - y \right| \leq \left| x \right| + \left| y \right|`,
  },
  { label: 'Floor', latex: String.raw`\lfloor \pi \rfloor = 3, \quad \lfloor e \rfloor = 2` },
  { label: 'Ceil', latex: String.raw`\lceil \sqrt{2} \rceil = 2` },
  { label: 'Angle brackets', latex: String.raw`\langle u, v \rangle = \sum_i u_i v_i` },
  {
    label: 'Big parens',
    latex: String.raw`\left( \left[ a + b \right] \cdot \left( c - d \right) \right)`,
  },
  {
    label: 'Mixed',
    latex: String.raw`\left[ \frac{1}{2}\left(\frac{a}{b} + \frac{b}{a}\right) \right]`,
  },
  { label: 'Norm', latex: String.raw`\left\| \vec{v} \right\| = \sqrt{v_x^2 + v_y^2}` },
];

// ---------------------------------------------------------------------------
// Page 8 — Matrices
// ---------------------------------------------------------------------------

const PAGE8: ExRow[] = [
  { label: 'Plain matrix', latex: String.raw`\begin{pmatrix} a & b \\ c & d \end{pmatrix}` },
  {
    label: 'Determinant',
    latex: String.raw`\det\begin{vmatrix} a & b \\ c & d \end{vmatrix} = ad - bc`,
  },
  {
    label: '3×3 matrix',
    latex: String.raw`\begin{bmatrix} 1 & 0 & 0 \\ 0 & 1 & 0 \\ 0 & 0 & 1 \end{bmatrix}`,
  },
  {
    label: 'Row vector',
    latex: String.raw`\mathbf{v} = \begin{pmatrix} v_1 & v_2 & v_3 \end{pmatrix}`,
  },
  {
    label: 'Col vector',
    latex: String.raw`\mathbf{u} = \begin{pmatrix} u_1 \\ u_2 \\ u_3 \end{pmatrix}`,
  },
  {
    label: 'System',
    latex: String.raw`\left\{ \begin{matrix} 2x + y = 5 \\ x - y = 1 \end{matrix} \right.`,
  },
  { label: 'Block matrix', latex: String.raw`M = \begin{pmatrix} A & B \\ 0 & D \end{pmatrix}` },
];

// ---------------------------------------------------------------------------
// Page 9 — Negated Relations & Operators
// ---------------------------------------------------------------------------

const PAGE9: ExRow[] = [
  { label: 'Negated leq', latex: String.raw`a \nleq b` },
  { label: 'Negated geq', latex: String.raw`a \ngeq b` },
  { label: 'Negated less', latex: String.raw`a \nless b` },
  { label: 'Negated gtr', latex: String.raw`a \ngtr b` },
  { label: 'Negated sim', latex: String.raw`a \nsim b` },
  { label: 'Negated approx', latex: String.raw`a \napprox b` },
  { label: 'Negated cong', latex: String.raw`a \ncong b` },
  { label: 'Triangle', latex: String.raw`\triangle ABC` },
  { label: 'Square', latex: String.raw`\square` },
  { label: 'Checkmark', latex: String.raw`\checkmark` },
  { label: 'Bullet', latex: String.raw`a \bullet b` },
];

// ---------------------------------------------------------------------------
// Page 10 — Bra-ket & Accents
// ---------------------------------------------------------------------------

const PAGE10: ExRow[] = [
  { label: 'lvert rvert', latex: String.raw`\lvert x \rvert` },
  { label: 'lVert rVert', latex: String.raw`\lVert x \rVert` },
  { label: 'Bra-ket', latex: String.raw`\langle \psi \rvert` },
  { label: 'Ket-bra', latex: String.raw`\lvert \psi \rangle` },
  { label: 'dddot', latex: String.raw`\dddot{x}` },
  { label: 'ddddot', latex: String.raw`\ddddot{x}` },
  { label: 'Overbrace', latex: String.raw`\overbrace{a + b + c}` },
  { label: 'Underscore', latex: String.raw`\underbrace{a + b + c}` },
  { label: 'Overbrace lab', latex: String.raw`\overbrace{a + b + c}^{n \text{ terms}}` },
  { label: 'Underscore lab', latex: String.raw`\underbrace{a + b + c}_{n \text{ terms}}` },
];

// ---------------------------------------------------------------------------
// Page 11 — Stack, Cancel, Boxed
// ---------------------------------------------------------------------------

const PAGE11: ExRow[] = [
  { label: 'Stackrel', latex: String.raw`A \stackrel{B}{=} C` },
  { label: 'Overset', latex: String.raw`\overset{a}{x}` },
  { label: 'Underset', latex: String.raw`\underset{a}{x}` },
  { label: 'Cancel', latex: String.raw`\cancel{x}` },
  { label: 'Bcancel', latex: String.raw`\bcancel{x}` },
  { label: 'Xcancel', latex: String.raw`\xcancel{x}` },
  { label: 'Boxed', latex: String.raw`\boxed{P(x)}` },
  { label: 'Xrightarrow', latex: String.raw`A \xrightarrow{x}` },
  { label: 'Xleftarrow', latex: String.raw`A \xleftarrow{y}` },
  { label: 'Xarrow both', latex: String.raw`A \xrightarrow[\text{below}]{\text{above}} B` },
];

// ---------------------------------------------------------------------------
// Page 12 — Alignment demonstration
// ---------------------------------------------------------------------------

function drawAlignmentPage(pdf: PdfGen): void {
  const page = addPage(pdf, 'Page 9 — Alignment (Left / Center / Right)');
  const lFont = new PdfFont(PdfFont.FONT_NAME, 8);
  const innerW = PdfSize.A4.width - 2 * MARGIN;
  const cutPen = new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5);

  const aligns: [ETextAlign, string][] = [
    [ETextAlign.CenterLeft, 'CenterLeft'],
    [ETextAlign.Center, 'Center'],
    [ETextAlign.CenterRight, 'CenterRight'],
    [ETextAlign.TopLeft, 'TopLeft'],
    [ETextAlign.TopCenter, 'TopCenter'],
    [ETextAlign.TopRight, 'TopRight'],
    [ETextAlign.BottomLeft, 'BottomLeft'],
    [ETextAlign.BottomCenter, 'BottomCenter'],
    [ETextAlign.BottomRight, 'BottomRight'],
  ];

  const formulas = [
    String.raw`E = mc^2`,
    String.raw`\frac{a}{b}`,
    String.raw`\sqrt{x^2 + 1}`,
    String.raw`\sum_{n=1}^{\infty} \frac{1}{n}`,
    String.raw`\int_0^1 x^2\,dx = \frac{1}{3}`,
    String.raw`\alpha + \beta = \gamma`,
    String.raw`a \leq b \geq c`,
    String.raw`A \Rightarrow B`,
    String.raw`\overline{AB}`,
  ];

  const rowH = MM(16);
  const labelW = MM(25);
  const mathW = innerW - labelW - MM(2);

  let y = MM(16);
  for (let i = 0; i < aligns.length; i++) {
    const [align, name] = aligns[i];
    const formula = formulas[i % formulas.length];

    const labelRect = new PdfRect(MARGIN, y, labelW, rowH);
    const mathRect = new PdfRect(MARGIN + labelW + MM(2), y, mathW, rowH);

    page.drawString(name, lFont, PdfBrush.Black, null, labelRect, ETextAlign.CenterLeft);
    page.drawRectangle(cutPen, null, mathRect);
    page.drawLatex(formula, mathRect, align, 14, PdfBrush.Black);
    y += rowH + MM(2);
  }
}

// ---------------------------------------------------------------------------
// Page 10 — Font size demonstration
// ---------------------------------------------------------------------------

function drawSizeDemoPage(pdf: PdfGen): void {
  const page = addPage(pdf, 'Page 10 — Font Sizes');
  const lFont = new PdfFont(PdfFont.FONT_NAME, 8);
  const innerW = PdfSize.A4.width - 2 * MARGIN;
  const cutPen = new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5);
  const labelW = MM(16);
  const GAP = MM(2);
  const VPAD = MM(2);

  const sizes = [8, 10, 12, 14, 16, 18, 20, 24, 28, 32];
  const formula = String.raw`x = \frac{-b \pm \sqrt{b^2 - 4ac}}{2a}`;

  let y = MM(14);
  for (const size of sizes) {
    const measured = page.measureLatex(formula, size);
    const rowH = measured.height + VPAD * 2;

    const labelRect = new PdfRect(MARGIN, y, labelW, rowH);
    const mathRect = new PdfRect(MARGIN + labelW + MM(2), y, innerW - labelW - MM(2), rowH);

    page.drawString(`${size} pt`, lFont, PdfBrush.Black, null, labelRect, ETextAlign.CenterLeft);
    page.drawRectangle(cutPen, null, mathRect);
    page.drawLatex(formula, mathRect, ETextAlign.CenterLeft, size, PdfBrush.Black);
    y += rowH + GAP;
  }
}

// ---------------------------------------------------------------------------
// Generator
// ---------------------------------------------------------------------------

export class TestLatex implements ILayoutBaseTestGen {
  async generate(pdf: PdfGen, opts: OptsBaseTest): Promise<void> {
    // Page 1
    const p1 = addPage(pdf, 'Page 1 — Basic Math & Fractions');
    drawRows(p1, PAGE1, MM(16));

    // Page 2
    const p2 = addPage(pdf, 'Page 2 — Greek Letters & Letterlike Symbols');
    drawRows(p2, PAGE2, MM(16));

    // Page 3
    const p3 = addPage(pdf, 'Page 3 — Relations & Set Theory');
    drawRows(p3, PAGE3, MM(16));

    // Page 4
    const p4 = addPage(pdf, 'Page 4 — Arrows');
    drawRows(p4, PAGE4, MM(16));

    // Page 5 — display style (limits above/below)
    // rowH=MM(22): display-style operators with limits above/below need extra vertical space
    const p5 = addPage(pdf, 'Page 5 — Functions, Limits & Integrals (display style)');
    drawRows(p5, PAGE5, MM(16), MM(22), MM(38), true);

    // Page 6
    const p6 = addPage(pdf, 'Page 6 — Accents, Dots, Spaces & Misc');
    drawRows(p6, PAGE6, MM(16));

    // Page 7
    const p7 = addPage(pdf, 'Page 7 — Brackets & Delimiters');
    drawRows(p7, PAGE7, MM(16));

    // Page 8
    const p8 = addPage(pdf, 'Page 8 — Matrices');
    drawRows(p8, PAGE8, MM(16), MM(18));

    // Page 9 — Negated Relations & Operators
    const p9 = addPage(pdf, 'Page 9 — Negated Relations & Operators');
    drawRows(p9, PAGE9, MM(16), MM(18));

    // Page 10 — Bra-ket & Accents
    const p10 = addPage(pdf, 'Page 10 — Bra-ket & Accents');
    drawRows(p10, PAGE10, MM(16), MM(18));

    // Page 11 — Stack, Cancel, Boxed
    const p11 = addPage(pdf, 'Page 11 — Stack, Cancel, Boxed');
    drawRows(p11, PAGE11, MM(16), MM(18));

    // Page 12 — alignment
    drawAlignmentPage(pdf);

    // Page 13 — font sizes
    drawSizeDemoPage(pdf);
  }
}
